import type { AlphaStylePick } from "@nlc/shared-types";

import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useRef } from "react";

import { AlphaApiError, createAlphaCreation } from "../api/alphaRuns.api";

const SUBMIT_ERROR_MESSAGES: Record<string, string> = {
  NO_ALPHA_PLAN: "Your project doesn't have an AI Ad Studio plan yet.",
  NO_CREDITS: "You've used all your creations for this period.",
  RENEWAL_PENDING: "Your plan is renewing. Please try again in a few minutes.",
};

const GENERIC_SUBMIT_ERROR = "Couldn't submit your brief. Please try again.";

function submitErrorMessage(error: Error | null): null | string {
  if (!error) return null;

  return (
    (error instanceof AlphaApiError &&
      error.code &&
      SUBMIT_ERROR_MESSAGES[error.code]) ||
    GENERIC_SUBMIT_ERROR
  );
}

export function useSubmitAlphaCreation(requestId: null | number): {
  errorMessage: null | string;
  isSubmitting: boolean;
  submit: (picks?: AlphaStylePick[]) => void;
} {
  const navigate = useNavigate();
  const idempotencyKey = useRef<null | string>(null);

  const goToAlphaPage = () =>
    navigate({
      params: { requestId: requestId! },
      to: "/requests/$requestId/alpha",
      viewTransition: true,
    });

  const mutation = useMutation({
    mutationFn: (picks: AlphaStylePick[]) => {
      idempotencyKey.current ??= crypto.randomUUID();

      return createAlphaCreation({
        idempotencyKey: idempotencyKey.current,
        picks,
        requestId: requestId!,
      });
    },
    onError: (error) => {
      // Only transient failures retry the same attempt; a 4xx is a final answer.
      if (error instanceof AlphaApiError && error.status < 500) {
        idempotencyKey.current = null;
      }

      if (error instanceof AlphaApiError && error.code === "RUN_ACTIVE") {
        void goToAlphaPage();
      }
    },
    onSuccess: () => {
      idempotencyKey.current = null;
      void goToAlphaPage();
    },
  });

  return {
    errorMessage:
      mutation.error instanceof AlphaApiError &&
      mutation.error.code === "RUN_ACTIVE"
        ? null
        : submitErrorMessage(mutation.error),
    isSubmitting: mutation.isPending,
    submit: (picks = []) => {
      if (requestId) mutation.mutate(picks);
    },
  };
}
